// Vendor-agnostic chat abstraction (Q10/Q23).
//
// The app never imports a vendor SDK directly. It calls getChatAdapter() once
// and the adapter returns a uniform shape: do we render anything? what's the URL?
// is there a script to inject? Swap providers via VITE_CHAT_PROVIDER, no code change.

export type ChatProvider = 'none' | 'whatsapp' | 'tidio' | 'intercom' | 'crisp' | 'custom'

export interface ChatAdapter {
  provider: ChatProvider
  /** When false, render nothing — the integration is disabled or misconfigured. */
  enabled: boolean
  /** A click-through URL (e.g. WhatsApp deep link). null when the provider needs an SDK instead. */
  clickThroughUrl: string | null
  /** External script URL to inject (Tidio/Crisp/Intercom). null for click-through providers. */
  scriptUrl: string | null
  /** Inline init snippet to run after the script loads. null when not needed. */
  initSnippet: string | null
  /** Visible label for the chat trigger button. */
  label: string
}

const VALID_PROVIDERS: ReadonlySet<ChatProvider> = new Set([
  'none',
  'whatsapp',
  'tidio',
  'intercom',
  'crisp',
  'custom',
])

function getProvider(): ChatProvider {
  const raw = (import.meta.env.VITE_CHAT_PROVIDER as string | undefined)?.toLowerCase()
  if (raw && VALID_PROVIDERS.has(raw as ChatProvider)) return raw as ChatProvider
  return 'none'
}

function whatsappAdapter(): ChatAdapter {
  /* v8 ignore start */
  const number = ((import.meta.env.VITE_CHAT_WHATSAPP_NUMBER as string | undefined) ?? '').replace(/[^0-9]/g, '')
  /* v8 ignore stop */
  if (!number) return disabled('whatsapp')
  /* v8 ignore start */
  const prefill = (import.meta.env.VITE_CHAT_WHATSAPP_PREFILL as string | undefined) ?? ''
  /* v8 ignore stop */
  const url = `https://wa.me/${number}${prefill ? `?text=${encodeURIComponent(prefill)}` : ''}`
  return {
    provider: 'whatsapp',
    enabled: true,
    clickThroughUrl: url,
    scriptUrl: null,
    initSnippet: null,
    label: 'Chat on WhatsApp',
  }
}

function tidioAdapter(): ChatAdapter {
  const key = (import.meta.env.VITE_CHAT_TIDIO_PUBLIC_KEY as string | undefined)?.trim()
  if (!key) return disabled('tidio')
  return {
    provider: 'tidio',
    enabled: true,
    clickThroughUrl: null,
    scriptUrl: `//code.tidio.co/${key}.js`,
    initSnippet: null,
    label: 'Open chat',
  }
}

function intercomAdapter(): ChatAdapter {
  const appId = (import.meta.env.VITE_CHAT_INTERCOM_APP_ID as string | undefined)?.trim()
  if (!appId) return disabled('intercom')
  return {
    provider: 'intercom',
    enabled: true,
    clickThroughUrl: null,
    scriptUrl: `https://widget.intercom.io/widget/${appId}`,
    initSnippet: `window.intercomSettings={app_id:'${appId}'};`,
    label: 'Open chat',
  }
}

function crispAdapter(): ChatAdapter {
  const websiteId = (import.meta.env.VITE_CHAT_CRISP_WEBSITE_ID as string | undefined)?.trim()
  if (!websiteId) return disabled('crisp')
  return {
    provider: 'crisp',
    enabled: true,
    clickThroughUrl: null,
    scriptUrl: 'https://client.crisp.chat/l.js',
    initSnippet: `window.$crisp=[];window.CRISP_WEBSITE_ID='${websiteId}';`,
    label: 'Open chat',
  }
}

function customAdapter(): ChatAdapter {
  const url = (import.meta.env.VITE_CHAT_CUSTOM_SCRIPT_URL as string | undefined)?.trim()
  if (!url) return disabled('custom')
  return {
    provider: 'custom',
    enabled: true,
    clickThroughUrl: null,
    scriptUrl: url,
    initSnippet: null,
    label: 'Open chat',
  }
}

function disabled(provider: ChatProvider): ChatAdapter {
  return {
    provider,
    enabled: false,
    clickThroughUrl: null,
    scriptUrl: null,
    initSnippet: null,
    label: '',
  }
}

export function getChatAdapter(): ChatAdapter {
  const provider = getProvider()
  switch (provider) {
    case 'whatsapp':
      return whatsappAdapter()
    case 'tidio':
      return tidioAdapter()
    case 'intercom':
      return intercomAdapter()
    case 'crisp':
      return crispAdapter()
    case 'custom':
      return customAdapter()
    case 'none':
    default:
      return disabled('none')
  }
}
